import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

// Error message display duration
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = [];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);

    // Handle the main "Choose options" button
    this.refs.addToCartButton?.addEventListener(
      'click',
      this.#handleChooseOptionsClick
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
    }

    this.removeEventListener('pointerenter', this.#preloadImage);

    this.refs.addToCartButton?.removeEventListener(
      'click',
      this.#handleChooseOptionsClick
    );
  }

  /**
   * Handles the main "Choose options" button.
   *
   * When force variant selection is enabled, clicking the button
   * scrolls the customer to the main variant picker instead of
   * trying to add the product to the cart.
   */
  #handleChooseOptionsClick = (event) => {
    if (this.dataset.forceVariantSelection !== 'true') {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const productForm = this.closest('product-form-component');

    if (!productForm) {
      console.error('Product form component not found.');
      return;
    }

    const variantPicker = productForm.querySelector('variant-picker');

    if (!variantPicker) {
      console.error('Variant picker not found.');
      return;
    }

    // Scroll to the variant picker
    variantPicker.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });

    // Find the first available option
    const firstOption =
      variantPicker.querySelector(
        'input[type="radio"]:not([disabled])'
      ) ||
      variantPicker.querySelector('select');

    // Focus the first option
    if (firstOption) {
      setTimeout(() => {
        firstOption.focus();
      }, 300);
    }
  };

  /**
   * Disables the add to cart button.
   */
  disable() {
    // NEVER disable the Choose Options button
    if (this.dataset.forceVariantSelection === 'true') {
      return;
    }

    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   *
   * @param {MouseEvent & {target: HTMLElement}} event
   */
  handleClick(event) {
    const form = this.closest('form');

    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm =
      /** @type {ProductFormComponent | null} */ (
        this.closest('product-form-component')
      );

    const quantitySelector = productForm?.refs.quantitySelector;

    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        return;
      }
    }

    if (
      this.refs.addToCartButton.dataset.puppet !== 'true'
    ) {
      const animationEnabled =
        this.dataset.addToCartAnimation === 'true';

      if (
        animationEnabled &&
        !event.target.closest('.quick-add-modal')
      ) {
        this.#animateFlyToCart();
      }

      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;

    const cartIcon = document.querySelector(
      '.header-actions__cart-icon'
    );

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement =
      /** @type {FlyToCart} */ (
        document.createElement('fly-to-cart')
      );

    const flyToCartClass =
      addToCartButton.classList.contains('quick-add__button')
        ? 'fly-to-cart--quick'
        : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);

    flyToCartElement.style.setProperty(
      'background-image',
      `url(${image})`
    );

    flyToCartElement.style.setProperty(
      '--start-opacity',
      '0'
    );

    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    this.#resetTimeouts.forEach(
      (timeoutId) => clearTimeout(timeoutId)
    );

    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    await yieldToMainThread();

    await onAnimationEnd(addToCartButton);

    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      const index =
        this.#resetTimeouts.indexOf(timeoutId);

      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define(
    'add-to-cart-component',
    AddToCartComponent
  );
}

/**
 * Product Form Component
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId
 * @property {AddToCartComponent | undefined} addToCartButtonContainer
 * @property {HTMLElement | undefined} addToCartTextError
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer
 * @property {HTMLElement} liveRegion
 * @property {HTMLElement | undefined} quantityLabelCartCount
 * @property {HTMLElement | undefined} quantityRules
 * @property {HTMLElement | undefined} productFormButtons
 * @property {HTMLElement | undefined} volumePricing
 * @property {any | undefined} quantitySelector
 * @property {HTMLElement | undefined} quantitySelectorWrapper
 * @property {HTMLElement | undefined} quantityLabel
 * @property {HTMLElement | undefined} pricePerItem
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];

  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** @type {Array<{variantId: string, quantity: number}>} */
  #addToCartQueue = [];

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    const target = this.closest(
      '.shopify-section, dialog, product-card'
    );

    target?.addEventListener(
      ThemeEvents.variantUpdate,
      this.#onVariantUpdate,
      { signal }
    );

    target?.addEventListener(
      ThemeEvents.variantSelected,
      this.#onVariantSelected,
      { signal }
    );

    document.addEventListener(
      ThemeEvents.cartUpdate,
      this.#onCartUpdate,
      { signal }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Updates quantity selector with cart data.
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput =
      /** @type {HTMLInputElement | null} */ (
        this.querySelector('input[name="id"]')
      );

    if (!variantIdInput?.value || !cart?.items) {
      return 0;
    }

    const cartItem = cart.items.find(
      (item) =>
        item.variant_id.toString() ===
        variantIdInput.value.toString()
    );

    const cartQty = cartItem ? cartItem.quantity : 0;

    const quantitySelector =
      /** @type {any | undefined} */ (
        this.querySelector(
          'quantity-selector-component'
        )
      );

    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches the current cart.
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput =
      /** @type {HTMLInputElement | null} */ (
        this.querySelector('input[name="id"]')
      );

    if (!variantIdInput?.value) return null;

    try {
      const response = await fetch('/cart.js');

      const cart = await response.json();

      this.#updateCartQuantityFromData(cart);

      return cart;
    } catch (error) {
      console.error(
        'Failed to fetch cart quantity:',
        error
      );

      return null;
    }
  }

  /**
   * Handles cart updates.
   */
  #onCartUpdate = async (event) => {
    if (
      event.detail?.sourceId === this.id ||
      event.detail?.data?.source ===
        'product-form-component'
    ) {
      return;
    }

    const cart = /** @type {Cart} */ (
      event.detail?.resource
    );

    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /**
   * Handles form submission.
   */
  handleSubmit(event) {
    event.preventDefault();

    // If this product requires a variant selection,
    // do not add anything until a variant is selected.
    if (
      this.dataset.forceVariantSelection === 'true' &&
      !this.refs.variantId?.value
    ) {
      const variantPicker =
        this.closest('product-form-component')
          ?.querySelector('variant-picker');

      if (variantPicker) {
        variantPicker.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });

        const firstOption =
          variantPicker.querySelector(
            'input[type="radio"]:not([disabled])'
          ) ||
          variantPicker.querySelector('select');

        if (firstOption) {
          setTimeout(() => {
            firstOption.focus();
          }, 300);
        }
      }

      return;
    }

    if (this.#variantChangeInProgress) {
      const intendedVariantId =
        this.#getIntendedVariantId();

      const quantity = this.#getQuantity();

      if (intendedVariantId) {
        this.#addToCartQueue.push({
          variantId: intendedVariantId,
          quantity,
        });
      }

      this.refs.addToCartButtonContainer
        ?.animateAddToCart?.();

      return;
    }

    this.#processAddToCart(
      undefined,
      undefined,
      event
    );
  }

  /**
   * Gets intended variant ID.
   */
  #getIntendedVariantId() {
    return (
      new URL(window.location.href)
        .searchParams.get('variant') ||
      this.refs.variantId?.value ||
      undefined
    );
  }

  /**
   * Gets quantity.
   */
  #getQuantity() {
    return (
      Number(
        this.refs.quantitySelector?.getValue?.()
      ) ||
      Number(this.dataset.quantityDefault) ||
      1
    );
  }

  /**
   * Processes add to cart.
   */
  #processAddToCart(
    overrideVariantId,
    overrideQuantity,
    event
  ) {
    const { addToCartTextError } = this.refs;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
    }

    const allAddToCartContainers =
      /** @type {NodeListOf<AddToCartComponent>} */ (
        this.querySelectorAll(
          'add-to-cart-component'
        )
      );

    if (!overrideVariantId) {
      const anyButtonDisabled =
        Array.from(
          allAddToCartContainers
        ).some(
          (container) =>
            container.refs.addToCartButton?.disabled
        );

      if (anyButtonDisabled) return;
    }

    const form = this.querySelector('form');

    if (!form) {
      throw new Error(
        'Product form element missing'
      );
    }

    if (
      !overrideVariantId &&
      this.refs.quantitySelector?.canAddToCart
    ) {
      const validation =
        this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        for (
          const container of allAddToCartContainers
        ) {
          container.disable();
        }

        const errorTemplate =
          this.dataset.quantityErrorMax || '';

        const errorMessage =
          errorTemplate.replace(
            '{{ maximum }}',
            validation.maxQuantity?.toString() || ''
          );

        if (addToCartTextError) {
          addToCartTextError.classList.remove(
            'hidden'
          );

          const textNode =
            addToCartTextError.childNodes[2];

          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            addToCartTextError.appendChild(
              document.createTextNode(
                errorMessage
              )
            );
          }

          this.#setLiveRegionText(
            errorMessage
          );

          if (this.#timeout) {
            clearTimeout(this.#timeout);
          }

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;

            addToCartTextError.classList.add(
              'hidden'
            );

            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          for (
            const container of allAddToCartContainers
          ) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

    if (overrideVariantId) {
      formData.set(
        'id',
        overrideVariantId
      );
    }

    if (overrideQuantity !== undefined) {
      formData.set(
        'quantity',
        overrideQuantity.toString()
      );
    }

    const cartItemsComponents =
      document.querySelectorAll(
        'cart-items-component'
      );

    const cartItemComponentsSectionIds = [];

    cartItemsComponents.forEach((item) => {
      if (
        item instanceof HTMLElement &&
        item.dataset.sectionId
      ) {
        cartItemComponentsSectionIds.push(
          item.dataset.sectionId
        );
      }
    });

    formData.append(
      'sections',
      cartItemComponentsSectionIds.join(',')
    );

    const fetchCfg = fetchConfig(
      'javascript',
      {
        body: formData,
      }
    );

    fetch(
      Theme.routes.cart_add_url,
      {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'text/html',
        },
      }
    )
      .then((response) =>
        response.json()
      )
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(
              form.getAttribute('id') || '',
              response.message,
              response.description,
              response.errors
            )
          );

          if (!addToCartTextError) {
            return;
          }

          addToCartTextError.classList.remove(
            'hidden'
          );

          const textNode =
            addToCartTextError.childNodes[2];

          if (textNode) {
            textNode.textContent =
              response.message;
          } else {
            addToCartTextError.appendChild(
              document.createTextNode(
                response.message
              )
            );
          }

          this.#setLiveRegionText(
            response.message
          );

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;

            addToCartTextError.classList.add(
              'hidden'
            );

            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          this.dispatchEvent(
            new CartAddEvent(
              {},
              this.id,
              {
                didError: true,
                source:
                  'product-form-component',
                itemCount:
                  Number(
                    formData.get('quantity')
                  ) ||
                  Number(
                    this.dataset
                      .quantityDefault
                  ),
                productId:
                  this.dataset.productId,
              }
            )
          );

          return;
        }

        const id = formData.get('id');

        if (addToCartTextError) {
          addToCartTextError.classList.add(
            'hidden'
          );

          addToCartTextError.removeAttribute(
            'aria-live'
          );
        }

        if (!id) {
          throw new Error(
            'Form ID is required'
          );
        }

        const anyAddToCartButton =
          allAddToCartContainers[0]
            ?.refs.addToCartButton;

        if (anyAddToCartButton) {
          const addedTextElement =
            anyAddToCartButton.querySelector(
              '.add-to-cart-text--added'
            );

          const addedText =
            addedTextElement?.textContent?.trim() ||
            Theme.translations.added;

          this.#setLiveRegionText(
            addedText
          );

          setTimeout(() => {
            this.#clearLiveRegionText();
          }, SUCCESS_MESSAGE_DISPLAY_DURATION);
        }

        const cart =
          await this.#fetchAndUpdateCartQuantity();

        this.dispatchEvent(
          new CartAddEvent(
            cart ?? undefined,
            id.toString(),
            {
              source:
                'product-form-component',
              itemCount:
                Number(
                  formData.get('quantity')
                ) ||
                Number(
                  this.dataset.quantityDefault
                ),
              productId:
                this.dataset.productId,
              sections:
                response.sections,
            }
          )
        );
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        if (event) {
          cartPerformance.measureFromEvent(
            'add:user-action',
            event
          );
        }
      });
  }

  /**
   * Processes queued add to cart items.
   */
  #processBatchAddToCart(items) {
    if (items.length === 0) return;

    const { addToCartTextError } =
      this.refs;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
    }

    const cartItemsComponents =
      document.querySelectorAll(
        'cart-items-component'
      );

    const cartItemComponentsSectionIds =
      [];

    for (
      const item of cartItemsComponents
    ) {
      if (
        item instanceof HTMLElement &&
        item.dataset.sectionId
      ) {
        cartItemComponentsSectionIds.push(
          item.dataset.sectionId
        );
      }
    }

    const payload = {
      items: items.map((item) => ({
        id: Number(item.variantId),
        quantity: item.quantity,
      })),
      sections:
        cartItemComponentsSectionIds.join(','),
    };

    fetch(
      Theme.routes.cart_add_url,
      {
        method: 'POST',
        headers: {
          'Content-Type':
            'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      }
    )
      .then((response) =>
        response.json()
      )
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(
              this.id,
              response.message,
              response.description,
              response.errors
            )
          );

          if (addToCartTextError) {
            addToCartTextError.classList.remove(
              'hidden'
            );

            const textNode =
              addToCartTextError.childNodes[2];

            if (textNode) {
              textNode.textContent =
                response.message;
            } else {
              addToCartTextError.appendChild(
                document.createTextNode(
                  response.message
                )
              );
            }

            this.#setLiveRegionText(
              response.message
            );

            this.#timeout = setTimeout(() => {
              addToCartTextError.classList.add(
                'hidden'
              );

              this.#clearLiveRegionText();
            }, ERROR_MESSAGE_DISPLAY_DURATION);
          }

          this.dispatchEvent(
            new CartAddEvent(
              {},
              this.id,
              {
                didError: true,
                source:
                  'product-form-component',
                itemCount:
                  items.reduce(
                    (sum, item) =>
                      sum + item.quantity,
                    0
                  ),
                productId:
                  this.dataset.productId,
              }
            )
          );

          return;
        }

        if (addToCartTextError) {
          addToCartTextError.classList.add(
            'hidden'
          );

          addToCartTextError.removeAttribute(
            'aria-live'
          );
        }

        const allAddToCartContainers =
          /** @type {NodeListOf<AddToCartComponent>} */ (
            this.querySelectorAll(
              'add-to-cart-component'
            )
          );

        const anyAddToCartButton =
          allAddToCartContainers[0]
            ?.refs.addToCartButton;

        if (anyAddToCartButton) {
          const addedTextElement =
            anyAddToCartButton.querySelector(
              '.add-to-cart-text--added'
            );

          const addedText =
            addedTextElement?.textContent?.trim() ||
            Theme.translations.added;

          this.#setLiveRegionText(
            addedText
          );

          setTimeout(() => {
            this.#clearLiveRegionText();
          }, SUCCESS_MESSAGE_DISPLAY_DURATION);
        }

        const cart =
          await this.#fetchAndUpdateCartQuantity();

        const totalQuantity =
          items.reduce(
            (sum, item) =>
              sum + item.quantity,
            0
          );

        this.dispatchEvent(
          new CartAddEvent(
            cart ?? undefined,
            this.id,
            {
              source:
                'product-form-component',
              itemCount:
                totalQuantity,
              productId:
                this.dataset.productId,
              sections:
                response.sections,
            }
          )
        );
      })
      .catch((error) => {
        console.error(error);
      });
  }

  /**
   * Updates quantity label.
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel =
      this.refs.quantityLabelCartCount;

    if (quantityLabel) {
      const inCartText =
        quantityLabel.textContent?.match(
          /\((\d+)\s+(.+)\)/
        );

      if (
        inCartText &&
        inCartText[2]
      ) {
        quantityLabel.textContent =
          `(${cartQty} ${inCartText[2]})`;
      }

      quantityLabel.classList.toggle(
        'hidden',
        cartQty === 0
      );
    }
  }

  /**
   * Sets screen reader live region.
   */
  #setLiveRegionText(text) {
    const liveRegion =
      this.refs.liveRegion;

    liveRegion.textContent = text;
  }

  /**
   * Clears screen reader live region.
   */
  #clearLiveRegionText() {
    const liveRegion =
      this.refs.liveRegion;

    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element.
   */
  #morphOrUpdateElement(
    currentElement,
    newElement,
    insertReferenceElement = null
  ) {
    if (
      currentElement &&
      newElement
    ) {
      morph(
        currentElement,
        newElement
      );
    } else if (
      currentElement &&
      !newElement
    ) {
      currentElement.remove();
    } else if (
      !currentElement &&
      newElement &&
      insertReferenceElement
    ) {
      insertReferenceElement.insertAdjacentElement(
        'beforebegin',
        newElement.cloneNode(true)
      );
    }
  }

  /**
   * Handles variant updates.
   */
  #onVariantUpdate = async (event) => {
    if (
      event.detail.data.newProduct
    ) {
      this.dataset.productId =
        event.detail.data.newProduct.id;
    } else if (
      event.detail.data.productId !==
      this.dataset.productId
    ) {
      return;
    }

    const { variantId } =
      this.refs;

    variantId.value =
      event.detail.resource?.id ?? '';

    this.#variantChangeInProgress =
      false;

    if (
      this.#addToCartQueue.length > 0
    ) {
      const queuedItems = [
        ...this.#addToCartQueue,
      ];

      this.#addToCartQueue = [];

      this.#processBatchAddToCart(
        queuedItems
      );
    }

    const {
      addToCartButtonContainer:
        currentAddToCartButtonContainer,
      acceleratedCheckoutButtonContainer,
    } = this.refs;

    const currentAddToCartButton =
      currentAddToCartButtonContainer
        ?.refs.addToCartButton;

    if (
      !currentAddToCartButtonContainer ||
      (
        !currentAddToCartButton &&
        !acceleratedCheckoutButtonContainer
      )
    ) {
      return;
    }

    // Update button state
    if (
      event.detail.resource == null ||
      event.detail.resource.available === false
    ) {
      // Do NOT disable Choose Options.
      if (
        currentAddToCartButtonContainer.dataset
          .forceVariantSelection !== 'true'
      ) {
        currentAddToCartButtonContainer.disable();
      }
    } else {
      currentAddToCartButtonContainer.enable();
    }

    // Get updated button HTML
    const newAddToCartButton =
      event.detail.data.html.querySelector(
        'product-form-component [ref="addToCartButton"]'
      );

    if (
      newAddToCartButton &&
      currentAddToCartButton
    ) {
      morph(
        currentAddToCartButton,
        newAddToCartButton
      );
    }

    // Accelerated checkout
    if (
      acceleratedCheckoutButtonContainer
    ) {
      if (
        event.detail.resource == null ||
        event.detail.resource.available === false
      ) {
        acceleratedCheckoutButtonContainer.setAttribute(
          'hidden',
          'true'
        );
      } else {
        acceleratedCheckoutButtonContainer.removeAttribute(
          'hidden'
        );
      }
    }

    // Update variant image
    if (event.detail.resource) {
      const productVariantMedia =
        event.detail.resource
          .featured_media
          ?.preview_image
          ?.src;

      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia +
            '&width=100'
        );
      }
    }

    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    const newQuantityInput =
      /** @type {HTMLInputElement | null} */ (
        event.detail.data.html.querySelector(
          'quantity-selector-component input[ref="quantityInput"]'
        )
      );

    if (
      quantitySelector?.updateConstraints &&
      newQuantityInput
    ) {
      quantitySelector.updateConstraints(
        newQuantityInput.min,
        newQuantityInput.max || null,
        newQuantityInput.step
      );
    }

    const newQuantityRules =
      event.detail.data.html.querySelector(
        '.quantity-rules'
      );

    const isQuantityRulesChanging =
      !!quantityRules !==
      !!newQuantityRules;

    const newPricePerItem =
      event.detail.data.html.querySelector(
        'price-per-item'
      );

    const isPricePerItemChanging =
      !!pricePerItem !==
      !!newPricePerItem;

    if (
      (
        isQuantityRulesChanging ||
        isPricePerItemChanging
      ) &&
      quantitySelector
    ) {
      const currentQuantityValue =
        quantitySelector.getValue?.();

      const newProductFormButtons =
        event.detail.data.html.querySelector(
          '.product-form-buttons'
        );

      if (
        productFormButtons &&
        newProductFormButtons
      ) {
        morph(
          productFormButtons,
          newProductFormButtons
        );

        const newQuantityInputElement =
          /** @type {HTMLInputElement | null} */ (
            event.detail.data.html.querySelector(
              'quantity-selector-component input[ref="quantityInput"]'
            )
          );

        if (
          this.refs.quantitySelector
            ?.updateConstraints &&
          newQuantityInputElement &&
          currentQuantityValue
        ) {
          this.refs.quantitySelector.setValue(
            currentQuantityValue
          );

          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max ||
              null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      const morphTargets = [
        [
          '.quantity-label',
          quantityLabel,
          quantitySelector,
        ],
        [
          '.quantity-rules',
          quantityRules,
          this.refs.productFormButtons,
        ],
        [
          'price-per-item',
          pricePerItem,
          quantitySelectorWrapper,
        ],
      ];

      for (
        const [
          selector,
          currentElement,
          fallback,
        ] of morphTargets
      ) {
        this.#morphOrUpdateElement(
          currentElement,
          event.detail.data.html.querySelector(
            selector
          ),
          fallback
        );
      }
    }

    const currentVolumePricing =
      this.refs.volumePricing;

    const newVolumePricing =
      event.detail.data.html.querySelector(
        'volume-pricing'
      );

    this.#morphOrUpdateElement(
      currentVolumePricing,
      newVolumePricing,
      this.refs.productFormButtons
    );

    const hasB2BFeatures =
      quantityRules ||
      newQuantityRules ||
      pricePerItem ||
      newPricePerItem ||
      currentVolumePricing ||
      newVolumePricing;

    if (!hasB2BFeatures) return;

    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Handles variant selection starting.
   */
  #onVariantSelected = () => {
    this.#variantChangeInProgress = true;
  };
}

if (
  !customElements.get(
    'product-form-component'
  )
) {
  customElements.define(
    'product-form-component',
    ProductFormComponent
  );
}